import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';

import Layout from '@/layout/Layout';
import { LessonProvider } from '@/context/LessonProvider';
import { ToastProvider } from '@/components/ui/toast';
import Loading from '@/pages/Loading';

// Lazy load components
const Home = lazy(() => import('@/pages/Home'));
const Lesson = lazy(() => import('@/components/lessons/Lesson'));
const Auth = lazy(() => import('@/auth/Auth'));
const Courses = lazy(() => import('@/components/courses/Courses'));
const ShoppingPage = lazy(() => import('@/components/courses/ShoppingPage'));
const NewsletterResignationPage = lazy(() => import('@/pages/NewsletterResignationPage'));
const NewsletterWithdrawResignationPage = lazy(() => import('@/pages/NewsletterWithdrawResignationPage'));
const NewsletterConfirmationPage = lazy(() => import('@/pages/NewsletterConfirmationPage'));
const RegistrationConfirmationPage = lazy(() => import('@/pages/RegistrationConfirmationPage'));
const NotFound = lazy(() => import('@/pages/404'));
const OAuthRedirectHandler = lazy(() => import('@/auth/OAuthRedirectHandler'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const DataDeletionInstructions = lazy(() => import('@/pages/DataDeletionInstructions'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const UserActionsPage = lazy(() => import('@/pages/UserActionsPage'));
const CartPage = lazy(() => import('@/pages/CartPage'));
const CheckoutForm = lazy(() => import('@/pages/CheckoutForm'));
const OrderSummary = lazy(() => import('@/pages/OrderSummaryPage'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmationPage'));
const OrdersPage = lazy(() => import('@/pages/OrdersPage'));
const StatisticsPage = lazy(() => import('../pages/StatisticsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));

export const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <ToastProvider>
        <Layout>
          <Suspense fallback={<Loading />}>
            <NotFound />
          </Suspense>
        </Layout>
      </ToastProvider>
    ),
  },
  {
    path: '/',
    element: (
      <ToastProvider>
        <Layout>
          <LessonProvider>
            <Suspense fallback={<Loading />}>
              <Home />
            </Suspense>
          </LessonProvider>
        </Layout>
      </ToastProvider>
    ),
  },
  {
    path: '/oauth-confirm',
    element: (
      <Layout>
        <LessonProvider>
          <ToastProvider>
            <Suspense fallback={<Loading />}>
              <OAuthRedirectHandler />
            </Suspense>
          </ToastProvider>
        </LessonProvider>
      </Layout>
    ),
  },
  {
    path: '/courses',
    element: (
      <Layout>
        <LessonProvider>
          <Suspense fallback={<Loading />}>
            <Courses />
          </Suspense>
        </LessonProvider>
      </Layout>
    ),
  },
  {
    path: '/courses/course-details/:courseId',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <ShoppingPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/cart',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <CartPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/cart/checkout',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <CheckoutForm />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/cart/order-summary',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <OrderSummary />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/orders/:orderId',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <OrderConfirmation />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/orders',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <OrdersPage/>
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/courses/:courseId/lesson/:lessonId',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <Lesson />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/auth',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <Auth />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/user-actions/*',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <UserActionsPage />
        </Suspense>
      </Layout>
    ),
    children: [
      {
        path: 'login',
        element: <UserActionsPage />,
      },
      {
        path: 'register',
        element: <UserActionsPage />,
      },
      {
        path: 'resend-email',
        element: <UserActionsPage />,
      },
      {
        path: 'email-notification',
        element: <UserActionsPage />,
      },
      {
        path: 'reset-password',
        element: <UserActionsPage />,
      },
    ],
  },
  {
    path: '/reset-password',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <ChangePasswordPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/newsletter-resignation',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <NewsletterResignationPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/newsletter-confirmation',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <NewsletterConfirmationPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/newsletter-enable',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <NewsletterWithdrawResignationPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/registration-confirmation',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <RegistrationConfirmationPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <PrivacyPolicy />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/data-deletion-instructions',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <DataDeletionInstructions />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/statistics',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <StatisticsPage />
        </Suspense>
      </Layout>
    ),
  },
  {
    path: '/settings',
    element: (
      <Layout>
        <Suspense fallback={<Loading />}>
          <SettingsPage />
        </Suspense>
      </Layout>
    ),
  },
]);
